import { ComponentProps, forwardRef, useMemo } from 'react';
import { Link as RouterLink, To } from 'react-router';

import css from './styles.module.css';

type BaseLinkProps = Omit<ComponentProps<'a'>, 'href'>;

type LinkProps = BaseLinkProps & {
  href: To;
  external?: boolean;
  variant?: 'primary' | 'secondary';
  state?: any;
};

const validProtocols = ['http', 'https', 'mailto', 'tel'];

function getPath(href: To) {
  if (typeof href === 'string') {
    return href;
  }
  return `${href.pathname}${href.search}${href.hash}`;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, children, variant, external, state, ...props },
  ref
) {
  const isExternal = useMemo(() => {
    if (typeof external !== 'undefined') {
      return external;
    }
    const path = getPath(href);
    return validProtocols.some((protocol) => path.startsWith(protocol));
  }, [href, external]);

  if (isExternal) {
    return (
      <a
        ref={ref}
        className={css.link}
        href={getPath(href)}
        target="_blank"
        rel="noopener noreferrer"
        data-variant={variant}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      ref={ref}
      className={css.link}
      to={href}
      data-variant={variant}
      state={state}
      {...props}
    >
      {children}
    </RouterLink>
  );
});
